import { Loader2, RefreshCw } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PerformanceCharts } from '@/components/performance/PerformanceCharts'
import { PerformanceDateRange } from '@/components/performance/PerformanceDateRange'
import { PerformanceFilters } from '@/components/performance/PerformanceFilters'
import { PerformanceRunTable } from '@/components/performance/PerformanceRunTable'
import { SegmentedControl } from '@/components/SegmentedControl'
import {
  SegmentedIndicator,
  segmentedOptionClassName,
} from '@/components/SegmentedIndicator'
import { Button } from '@/components/ui/button'
import type {
  PerformancePresetRange,
  PerformanceViewMode,
  ResolvedPerformanceSelection,
} from '@/lib/performance.types'
import { usePerformanceSeries } from '@/lib/performanceQueries'
import { parseUtcDate } from '@/lib/performanceUrl'
import { useSubmitRun } from '@/lib/queries'
import { usePerformanceSelection } from '@/lib/usePerformanceSelection'
import { usePerformanceViewMode } from '@/lib/usePerformanceViewMode'
import { useTrackerHref } from '@/provider/useTrackerHref'
import { useTrackerNavigate } from '@/provider/useTrackerNavigate'

const VIEW_INDICATOR = 'performance-view-mode-indicator'

const VIEW_MODES: readonly PerformanceViewMode[] = ['expanded', 'focused']

const VIEW_MODE_LABEL: Record<PerformanceViewMode, string> = {
  expanded: 'Expanded',
  focused: 'Focused',
}

const RANGE_LABEL: Record<PerformancePresetRange, string> = {
  '30d': 'the last 30 days',
  '90d': 'the last 90 days',
  '1y': 'the last year',
}

function customRangeDays(
  selection: ResolvedPerformanceSelection
): number | null {
  if (selection.range !== 'custom' || !selection.from || !selection.to) {
    return null
  }

  const from = parseUtcDate(selection.from)
  const to = parseUtcDate(selection.to)
  if (!from || !to) return null

  return Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1
}

// Widens 30d -> 90d -> 1y; a custom range widens to the next preset that actually covers it.
function nextWiderRange(
  selection: ResolvedPerformanceSelection
): PerformancePresetRange | null {
  if (selection.range === '30d') return '90d'
  if (selection.range === '90d') return '1y'
  if (selection.range === '1y') return null

  const days = customRangeDays(selection)
  if (days === null) return null
  if (days < 90) return '90d'
  if (days < 365) return '1y'
  return null
}

function CenteredMessage({
  tone,
  children,
}: {
  tone: 'muted' | 'destructive'
  children: ReactNode
}) {
  return (
    <p
      className={`py-16 text-center ${tone === 'destructive' ? 'text-destructive' : 'text-muted-foreground'}`}
    >
      {children}
    </p>
  )
}

// Reachable only when auth is present (routed under TrackerApp's authed branch).
export default function Performance() {
  const href = useTrackerHref()
  const navigate = useTrackerNavigate()
  const rerunRun = useSubmitRun({ forceFresh: true })
  const selection = usePerformanceSelection()
  const [viewMode, setViewMode] = usePerformanceViewMode()
  const seriesQuery = usePerformanceSeries(selection.filters)
  const selectedBrand = useMemo(
    () =>
      selection.brands.find(
        (brand) => brand.value === selection.selection?.brandId
      ),
    [selection.brands, selection.selection?.brandId]
  )
  const selectedCategory = useMemo(
    () =>
      selection.categories.find(
        (category) => category.value === selection.selection?.category
      ),
    [selection.categories, selection.selection?.category]
  )
  const selectedMarket = useMemo(
    () =>
      selection.markets.find(
        (market) => market.value === selection.selection?.market
      ),
    [selection.markets, selection.selection?.market]
  )

  if (selection.isLoading) {
    return (
      <main className="px-6 pb-20 mx-auto w-full max-w-[2000px] 2xl:px-12">
        <CenteredMessage tone="muted">
          Loading performance options…
        </CenteredMessage>
      </main>
    )
  }

  if (selection.isError) {
    return (
      <main className="px-6 pb-20 mx-auto w-full max-w-[2000px] 2xl:px-12">
        <CenteredMessage tone="destructive">
          Could not load performance options.
        </CenteredMessage>
      </main>
    )
  }

  if (!selection.selection) {
    return (
      <main className="px-6 pb-20 mx-auto w-full max-w-[2000px] 2xl:px-12">
        <section className="py-16 text-center space-y-4">
          <p className="text-muted-foreground">
            No tracked brand and category has performance data yet.
          </p>
          <div className="flex justify-center gap-3">
            <Button asChild>
              <Link to={href('/')}>Start a run</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={href('/schedules')}>Schedule one</Link>
            </Button>
          </div>
        </section>
      </main>
    )
  }

  const resolvedSelection = selection.selection
  const widerRange = nextWiderRange(resolvedSelection)
  const series = seriesQuery.data
  const hasTargetMarketSelection =
    (resolvedSelection.targetMarketIds?.length ?? 0) > 0

  async function rerun() {
    if (!selectedBrand || !selectedCategory) return

    const targetMarketIds = resolvedSelection.targetMarketIds
    const marketInput =
      targetMarketIds && targetMarketIds.length > 0
        ? { targetMarketIds }
        : selectedMarket
          ? { market: selectedMarket.label }
          : null

    if (!marketInput) return

    try {
      const result = await rerunRun.mutateAsync({
        brand: selectedBrand.label,
        category: selectedCategory.label,
        ...marketInput,
        liveSearch: resolvedSelection.search === 'live',
      })

      navigate(`/run/${result.runId}`)
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not start the rerun.'
      )
    }
  }

  return (
    <main className="px-6 pb-20 mx-auto w-full max-w-[2000px] 2xl:px-12">
      <section className="pt-8 pb-4 flex items-end justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-semibold">Performance</h1>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl label="View mode">
            {VIEW_MODES.map((mode) => {
              const active = viewMode === mode

              return (
                <label key={mode} className={segmentedOptionClassName(active)}>
                  {active && <SegmentedIndicator layoutId={VIEW_INDICATOR} />}
                  <input
                    type="radio"
                    name="performance-view-mode"
                    value={mode}
                    checked={active}
                    onChange={() => setViewMode(mode)}
                    className="sr-only"
                  />
                  <span className="relative">{VIEW_MODE_LABEL[mode]}</span>
                </label>
              )
            })}
          </SegmentedControl>
          <Link
            to={href('/schedules')}
            className="text-sm font-medium text-primary hover:underline"
          >
            Schedules
          </Link>
        </div>
      </section>

      <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
        <fieldset className="flex flex-wrap items-end gap-4">
          <legend className="sr-only">Selected query</legend>
          <PerformanceFilters
            selection={resolvedSelection}
            brands={selection.brands}
            categories={selection.categories}
            markets={selection.markets}
            targetMarkets={selection.targetMarkets}
            onBrandChange={selection.onBrandChange}
            onCategoryChange={selection.onCategoryChange}
            onMarketChange={selection.onMarketChange}
            onTargetMarketChange={selection.onTargetMarketChange}
            onSearchChange={selection.onSearchChange}
          />
          <Button
            type="button"
            variant="outline"
            size="lg"
            disabled={
              rerunRun.isPending ||
              !selectedBrand ||
              !selectedCategory ||
              (!selectedMarket && !hasTargetMarketSelection)
            }
            onClick={() => void rerun()}
          >
            {rerunRun.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <RefreshCw className="size-3.5" />
            )}
            Run again
          </Button>
        </fieldset>
        <PerformanceDateRange
          range={resolvedSelection.range}
          from={resolvedSelection.from}
          to={resolvedSelection.to}
          onRangeChange={selection.onRangeChange}
          onApplyCustom={selection.onApplyCustom}
        />
      </div>

      <div className="mt-8">
        {seriesQuery.isPending && (
          <CenteredMessage tone="muted">Loading performance…</CenteredMessage>
        )}

        {seriesQuery.isError && (
          <CenteredMessage tone="destructive">
            Could not load performance data.
          </CenteredMessage>
        )}

        {series?.kind === 'too_many' && (
          <div className="py-16 text-center space-y-2">
            <p className="text-muted-foreground">
              This range has too many measurements to chart.
            </p>
            <p className="text-sm text-muted-foreground">
              Narrow the date range to see a trend.
            </p>
          </div>
        )}

        {series?.kind === 'ready' && series.points.length === 0 && (
          <div className="py-16 text-center space-y-4">
            <p className="text-muted-foreground">
              No measurements in this range yet.
            </p>
            {widerRange ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => selection.onRangeChange(widerRange)}
              >
                Try {RANGE_LABEL[widerRange]}
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Run or schedule another measurement to build a trend here.
              </p>
            )}
          </div>
        )}

        {series?.kind === 'ready' && series.points.length > 0 && (
          <>
            <PerformanceCharts points={series.points} viewMode={viewMode} />
            <PerformanceRunTable points={series.points} />
          </>
        )}
      </div>
    </main>
  )
}
